export const TAXES_RULE_CATEGORY = "TAXES";
export const CUSTOM_TAX_RULE_TYPE = "CUSTOM_TAX";
export const APPLIES_TO_RULE_TYPES_KEY = "applies_to_rule_types";

export function isTaxesStandaloneCategory(category) {
  return String(category || "").toUpperCase() === TAXES_RULE_CATEGORY;
}

export function isCustomTaxRule(category, type) {
  return (
    isTaxesStandaloneCategory(category) &&
    String(type || "").toUpperCase() === CUSTOM_TAX_RULE_TYPE
  );
}

export function buildDefaultAppliesToRuleTypesMap(paramDef) {
  if (paramDef?.default && typeof paramDef.default === "object") {
    return { ...paramDef.default };
  }
  const map = {};
  (paramDef?.options || []).forEach((opt) => {
    if (opt?.value) map[opt.value] = opt.default === true;
  });
  return map;
}

export function getAppliesToRuleTypeOptionDisplayLabel(opt, allOptions) {
  const base = opt.label || opt.value;
  const duplicateLabels = (allOptions || []).filter((o) => (o.label || o.value) === base).length > 1;
  if (!duplicateLabels) return base;
  const value = String(opt.value || "").toUpperCase();
  if (value.includes("INELIGIBLE")) return `${base} (Ineligible)`;
  if (value === "MARGIN" || value === "DISCOUNT") return `${base} (Eligible)`;
  return `${base} (${opt.value})`;
}

export function getAppliesToRuleTypeOptions(paramDef) {
  const allOptions = paramDef?.options || [];
  return allOptions.map((opt) => ({
    value: opt.value,
    label: getAppliesToRuleTypeOptionDisplayLabel(opt, allOptions),
  }));
}

export function getSelectedAppliesToRuleTypeValues(map) {
  if (!map || typeof map !== "object") return [];
  return Object.entries(map)
    .filter(([, enabled]) => enabled === true)
    .map(([key]) => key);
}

/** Human-readable labels for read-only displays (e.g. Applied Rules modal). */
export function formatAppliesToRuleTypeLabel(ruleTypeKey) {
  const key = String(ruleTypeKey || "").toUpperCase();
  const labels = {
    SPP: "SPP",
    TAX: "Tax",
    MARGIN: "Markup",
    DISCOUNT: "Discount",
    FIXED_COST: "Fixed Cost",
    AWS_CREDITS: "AWS Credits",
    AWS_PARTNER_SUPPORT: "AWS Partner Support",
    AWS_PARTNER_SUPPORT_INELIGIBLE: "AWS Partner Support (Ineligible)",
    MARGIN_INELIGIBLE: "Markup (Ineligible)",
    DISCOUNT_INELIGIBLE: "Discount (Ineligible)",
  };
  return labels[key] || String(ruleTypeKey || "").replace(/_/g, " ");
}

export function buildAppliesToRuleTypesMapFromSelection(selectedValues, paramDef) {
  const selected = new Set(selectedValues || []);
  const map = buildDefaultAppliesToRuleTypesMap(paramDef);
  Object.keys(map).forEach((key) => {
    map[key] = selected.has(key);
  });
  (paramDef?.options || []).forEach((opt) => {
    if (opt?.value) map[opt.value] = selected.has(opt.value);
  });
  return map;
}

export function buildDefaultRuleParametersFromTypeDef(typeDef) {
  const requiredParams = typeDef?.required_parameters || [];
  const defaultParams = {};
  const percentParam = requiredParams.find(
    (p) => p.type === "number" && (p.key === "percent" || String(p.label || "").toLowerCase().includes("percent"))
  );
  const percentKey = percentParam?.key ?? requiredParams.find((p) => p.type === "number")?.key;
  if (percentKey) {
    defaultParams._fullPartial = "full";
    defaultParams[percentKey] = 100;
  }
  const appliesParam = requiredParams.find((p) => p.key === APPLIES_TO_RULE_TYPES_KEY);
  if (appliesParam) {
    defaultParams[APPLIES_TO_RULE_TYPES_KEY] = buildDefaultAppliesToRuleTypesMap(appliesParam);
  }
  return defaultParams;
}

/** Shape expected by POST /rules for TAXES / CUSTOM_TAX configuration.parameters */
export function sanitizeCustomTaxParametersForApi(parameters) {
  const raw = parameters && typeof parameters === "object" ? { ...parameters } : {};
  const percentRaw = raw.percent;
  let percent =
    typeof percentRaw === "number" ? percentRaw : Number.parseFloat(String(percentRaw ?? "").replace(/[^0-9.-]/g, ""));
  if (!Number.isFinite(percent)) percent = 0;
  percent = Math.min(100, Math.max(0, percent));

  const appliesRaw = raw[APPLIES_TO_RULE_TYPES_KEY];
  const applies_to_rule_types = {};
  if (appliesRaw && typeof appliesRaw === "object" && !Array.isArray(appliesRaw)) {
    Object.entries(appliesRaw).forEach(([key, value]) => {
      if (key) applies_to_rule_types[key] = value === true;
    });
  }

  return {
    percent,
    applies_to_rule_types,
  };
}
